import { supabase } from "@/lib/supabase";
import { getVendorToken } from "@/lib/vendorAuth";
import { isCookieVendorAuthMode, vendorApiUrl } from "@/lib/vendorAuthMode";
import { vendorFetch } from "@/lib/vendorApi";
import type { ConsumerTier } from "@/lib/consumerTier";
import type { VendorPaidTier } from "@/lib/vendorTier";

export type SubscriptionOwnerType = "user" | "venue";
export type CheckoutTier = ConsumerTier | VendorPaidTier;
export type StripeAuthMode = "consumer" | "vendor";

export type SubscriptionRecord = {
  id: string;
  owner_type: SubscriptionOwnerType;
  owner_id: string;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  tier: string;
  billing_interval: "month" | "year";
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_start: string | null;
  trial_end: string | null;
  subscription_source: string;
  created_at: string;
  updated_at: string;
};

export const consumerSubscriptionQueryKey = (userId: string) =>
  ["consumer-subscription", userId] as const;

export const vendorSubscriptionQueryKey = (venueId: string) =>
  ["vendor-subscription", venueId] as const;

function supabaseUrl(): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new Error("Missing VITE_SUPABASE_URL");
  return url;
}

function supabaseAnonKey(): string {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) throw new Error("Missing VITE_SUPABASE_ANON_KEY");
  return key;
}

export function stripePublishableKey(): string | undefined {
  return import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
}

function stripeAuthModeForOwner(ownerType: SubscriptionOwnerType): StripeAuthMode {
  return ownerType === "venue" ? "vendor" : "consumer";
}

async function stripeEdgeHeaders(authMode: StripeAuthMode): Promise<HeadersInit> {
  const base: HeadersInit = {
    apikey: supabaseAnonKey(),
    "Content-Type": "application/json",
  };

  if (authMode === "vendor") {
    return {
      ...base,
      Authorization: `Bearer ${supabaseAnonKey()}`,
    };
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  return {
    ...base,
    Authorization: `Bearer ${session.access_token}`,
  };
}

async function stripeEdgeFetch(
  functionName: string,
  payload: Record<string, unknown>,
  authMode: StripeAuthMode,
): Promise<Response> {
  if (authMode === "vendor" && isCookieVendorAuthMode()) {
    return fetch(vendorApiUrl(`/proxy/${functionName}`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  const body =
    authMode === "vendor"
      ? (() => {
          const token = getVendorToken();
          if (!token) throw new Error("Not authenticated");
          return { token, ...payload };
        })()
      : payload;

  return fetch(`${supabaseUrl()}/functions/v1/${functionName}`, {
    method: "POST",
    headers: await stripeEdgeHeaders(authMode),
    body: JSON.stringify(body),
  });
}

export async function createCheckoutSession(params: {
  ownerType: SubscriptionOwnerType;
  ownerId: string;
  priceId: string;
  tier?: CheckoutTier;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<string> {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const successUrl = params.successUrl ?? `${origin}/settings?checkout=success`;
  const cancelUrl = params.cancelUrl ?? `${origin}/settings?checkout=canceled`;
  const authMode = stripeAuthModeForOwner(params.ownerType);

  const res = await stripeEdgeFetch(
    "create-checkout-session",
    {
      ownerType: params.ownerType,
      ownerId: params.ownerId,
      priceId: params.priceId,
      tier: params.tier,
      successUrl,
      cancelUrl,
    },
    authMode,
  );

  const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !body.url) {
    throw new Error(body.error ?? "Checkout session could not be created");
  }

  if (typeof window !== "undefined") {
    window.location.href = body.url;
  }

  return body.url;
}

export async function cancelSubscription(
  subscriptionId: string,
  options?: { authMode?: StripeAuthMode },
): Promise<void> {
  const authMode = options?.authMode ?? "consumer";

  const res = await stripeEdgeFetch(
    "cancel-subscription",
    { subscriptionId },
    authMode,
  );

  const body = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
  if (!res.ok || !body.success) {
    throw new Error(body.error ?? "Subscription could not be canceled");
  }
}

export async function updateSubscriptionTier(
  newTier: CheckoutTier,
  options?: { ownerType?: SubscriptionOwnerType; ownerId?: string; authMode?: StripeAuthMode },
): Promise<{ effectiveDate: string | null }> {
  const ownerType = options?.ownerType ?? "user";
  const authMode = options?.authMode ?? stripeAuthModeForOwner(ownerType);

  const res = await stripeEdgeFetch(
    "update-subscription-tier",
    {
      newTier,
      ownerType,
      ownerId: options?.ownerId,
    },
    authMode,
  );

  const body = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    effectiveDate?: string | null;
    error?: string;
  };

  if (!res.ok || !body.success) {
    throw new Error(body.error ?? "Tier change could not be scheduled");
  }

  return { effectiveDate: body.effectiveDate ?? null };
}

const SUBSCRIPTION_SELECT =
  "id, owner_type, owner_id, stripe_subscription_id, stripe_price_id, tier, billing_interval, status, current_period_start, current_period_end, cancel_at_period_end, trial_start, trial_end, subscription_source, created_at, updated_at";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchConsumerActiveSubscription(ownerId: string): Promise<SubscriptionRecord | null> {
  // Freemium rows (tier=free) are not a paid/trial subscription for Abo/checkout UI.
  const { data, error } = await supabase
    .from("subscriptions")
    .select(SUBSCRIPTION_SELECT)
    .eq("owner_type", "user")
    .eq("owner_id", ownerId)
    .neq("tier", "free")
    .in("status", ["active", "trialing", "past_due"])
    .order("created_at", { ascending: false })
    .limit(1);

  if (error) throw error;
  const row = data?.[0];
  if (!row) return null;

  return row as SubscriptionRecord;
}

async function fetchVendorActiveSubscription(venueId: string): Promise<SubscriptionRecord | null> {
  const body = await vendorFetch<{ subscription: SubscriptionRecord | null }>(
    "vendor-venue",
    "get_subscription",
    { venueId },
  );
  return body.subscription ?? null;
}

export async function fetchActiveSubscription(
  ownerType: SubscriptionOwnerType,
  ownerId: string,
  options?: { retry?: boolean; retryAttempts?: number; retryDelayMs?: number },
): Promise<SubscriptionRecord | null> {
  const attempts = options?.retry ? (options.retryAttempts ?? 5) : 1;
  const delayMs = options?.retryDelayMs ?? 800;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const subscription =
      ownerType === "venue"
        ? await fetchVendorActiveSubscription(ownerId)
        : await fetchConsumerActiveSubscription(ownerId);

    if (subscription || attempt === attempts - 1) {
      return subscription;
    }

    await sleep(delayMs);
  }

  return null;
}

export { formatSubscriptionDate } from "@/lib/format";
